/**
 * Hook para gerenciar widgets personalizáveis do dashboard
 * 
 * @author Equipe GestaoeCRM
 * @date 19/12/2025
 */

import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';

export type WidgetId = 
  | 'stats-cards'
  | 'charts'
  | 'areas'
  | 'active-os-grid'
  | 'work-session-reports'
  | 'project-cards'
  | 'recent-projects'
  | 'analyst-reports';

export interface WidgetConfig {
  id: WidgetId;
  name: string;
  description: string;
  visible: boolean;
  order: number;
  roles: ('ADMIN' | 'GESTOR' | 'ANALISTA')[];
}

const DEFAULT_WIDGETS: Record<WidgetId, Omit<WidgetConfig, 'visible' | 'order'>> = {
  'stats-cards': {
    id: 'stats-cards',
    name: 'Cards de Estatísticas',
    description: 'Métricas principais do dashboard',
    roles: ['ADMIN', 'GESTOR', 'ANALISTA'],
  },
  'charts': {
    id: 'charts',
    name: 'Gráficos de OS',
    description: 'Visualização de distribuição de OS por área e status',
    roles: ['ADMIN'],
  },
  'areas': {
    id: 'areas',
    name: 'Visualização por Áreas',
    description: 'Navegação hierárquica por áreas da empresa',
    roles: ['ADMIN'],
  },
  'active-os-grid': {
    id: 'active-os-grid',
    name: 'Jobs/Tasks Ativos',
    description: 'Grade de todos os jobs/tasks em andamento',
    roles: ['ADMIN'],
  },
  'work-session-reports': {
    id: 'work-session-reports',
    name: 'Relatórios de Sessões',
    description: 'Estatísticas de trabalho da empresa',
    roles: ['ADMIN'],
  },
  'project-cards': {
    id: 'project-cards',
    name: 'Cards de Projetos',
    description: 'Visualização de projetos com jobs/tasks ativos',
    roles: ['GESTOR'],
  },
  'recent-projects': {
    id: 'recent-projects',
    name: 'Projetos Recentes',
    description: 'Últimos projetos em que você trabalhou',
    roles: ['ANALISTA'],
  },
  'analyst-reports': {
    id: 'analyst-reports',
    name: 'Relatórios Pessoais',
    description: 'Métricas de produtividade pessoal',
    roles: ['ANALISTA'],
  },
};

/**
 * Ordem padrão dos widgets por role
 */
const DEFAULT_ORDER: Record<'ADMIN' | 'GESTOR' | 'ANALISTA', WidgetId[]> = {
  ADMIN: [
    'stats-cards',
    'charts',
    'areas',
    'active-os-grid',
    'work-session-reports',
  ],
  GESTOR: [
    'stats-cards',
    'project-cards',
  ],
  ANALISTA: [
    'stats-cards',
    'recent-projects',
    'analyst-reports',
  ],
};

export function useDashboardWidgets() {
  const { user } = useAuth();
  const [widgets, setWidgets] = useState<WidgetConfig[]>([]);

  // Inicializar widgets baseado no role
  useEffect(() => {
    if (!user?.role) return;

    const role = user.role as 'ADMIN' | 'GESTOR' | 'ANALISTA';
    const defaultOrder = DEFAULT_ORDER[role] || [];
    
    // Carregar configuração salva do localStorage
    const savedConfig = localStorage.getItem(`dashboard-widgets-${user.id}`);
    let savedWidgets: Partial<Record<WidgetId, { visible: boolean; order: number }>> = {};
    
    if (savedConfig) {
      try {
        savedWidgets = JSON.parse(savedConfig);
      } catch (error) {
        console.error('Erro ao carregar configuração de widgets:', error);
      }
    }

    // Construir lista de widgets
    const widgetList: WidgetConfig[] = defaultOrder.map((widgetId, index) => {
      const defaultConfig = DEFAULT_WIDGETS[widgetId];
      const saved = savedWidgets[widgetId];
      
      return {
        ...defaultConfig,
        visible: saved?.visible ?? true,
        order: saved?.order ?? index,
      };
    });

    setWidgets(widgetList);
  }, [user?.id, user?.role]);

  /**
   * Alternar visibilidade de um widget
   */
  const toggleWidget = (widgetId: WidgetId) => {
    if (!user?.id) return;

    setWidgets((prev) => {
      const updated = prev.map((widget) =>
        widget.id === widgetId
          ? { ...widget, visible: !widget.visible }
          : widget
      );

      // Salvar no localStorage
      const config: Partial<Record<WidgetId, { visible: boolean; order: number }>> = {};
      updated.forEach((widget) => {
        config[widget.id] = {
          visible: widget.visible,
          order: widget.order,
        };
      });

      localStorage.setItem(`dashboard-widgets-${user.id}`, JSON.stringify(config));

      return updated;
    });
  };

  /**
   * Reordenar widgets
   */
  const reorderWidgets = (widgetId: WidgetId, newOrder: number) => {
    if (!user?.id) return;

    setWidgets((prev) => {
      const updated = [...prev];
      const widgetIndex = updated.findIndex((w) => w.id === widgetId);
      
      if (widgetIndex === -1) return prev;

      const [widget] = updated.splice(widgetIndex, 1);
      widget.order = newOrder;
      updated.splice(newOrder, 0, widget);

      // Atualizar ordens
      updated.forEach((w, index) => {
        w.order = index;
      });

      // Salvar no localStorage
      const config: Partial<Record<WidgetId, { visible: boolean; order: number }>> = {};
      updated.forEach((w) => {
        config[w.id] = {
          visible: w.visible,
          order: w.order,
        };
      });

      localStorage.setItem(`dashboard-widgets-${user.id}`, JSON.stringify(config));

      return updated;
    });
  };

  /**
   * Resetar widgets para padrão
   */
  const resetWidgets = () => {
    if (!user?.id || !user?.role) return;

    const role = user.role as 'ADMIN' | 'GESTOR' | 'ANALISTA';
    const defaultOrder = DEFAULT_ORDER[role] || [];

    const widgetList: WidgetConfig[] = defaultOrder.map((widgetId, index) => ({
      ...DEFAULT_WIDGETS[widgetId],
      visible: true,
      order: index,
    }));

    setWidgets(widgetList);
    localStorage.removeItem(`dashboard-widgets-${user.id}`);
  };

  /**
   * Obter widgets visíveis ordenados
   */
  const visibleWidgets = widgets
    .filter((w) => w.visible)
    .sort((a, b) => a.order - b.order);

  /**
   * Verificar se widget está visível
   */
  const isWidgetVisible = (widgetId: WidgetId): boolean => {
    return widgets.find((w) => w.id === widgetId)?.visible ?? true;
  };

  return {
    widgets,
    visibleWidgets,
    toggleWidget,
    reorderWidgets,
    resetWidgets,
    isWidgetVisible,
  };
}

